import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Tabs, Tab, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, Checkbox, Tooltip,
  CircularProgress, Alert, Divider, InputAdornment, List, ListItem,
  ListItemText, ListItemIcon,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MapIcon from '@mui/icons-material/Map';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import apiClient from '../api/client';
import PlanView from './PlanView';

const fmt = iso => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = d => d ? new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const statusColor = s => ({ available: 'success', pending: 'warning', sold: 'error' }[s] || 'default');

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [allProperties, setAllProperties] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addPropOpen, setAddPropOpen] = useState(false);
  const [propFilter, setPropFilter] = useState('');
  const [propTypeFilter, setPropTypeFilter] = useState('');
  const [selectedPropIds, setSelectedPropIds] = useState(new Set());
  const [copyPlanOpen, setCopyPlanOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState(null);
  const [copyDate, setCopyDate] = useState('');
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [viewPlanId, setViewPlanId] = useState(null);
  const [planStops, setPlanStops] = useState({});
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get(`/clients/${id}`),
      apiClient.get('/properties'),
    ]).then(([cr, pr]) => {
      setClient(cr.data.data);
      setAllProperties(pr.data.data);
    }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const assignedIds = new Set(client?.clientProperties?.map(cp => cp.property_id) ?? []);
  const unassignedProperties = allProperties.filter(p => !assignedIds.has(p.id));

  const handleAssign = async () => {
    if (!selectedPropIds.size) return;
    await Promise.all([...selectedPropIds].map(pid =>
      apiClient.post(`/clients/${id}/properties`, { property_id: pid })
    ));
    setAddPropOpen(false);
    setSelectedPropIds(new Set());
    setPropFilter('');
    setPropTypeFilter('');
    load();
  };

  const togglePropSelect = (pid) => setSelectedPropIds(prev => {
    const next = new Set(prev);
    next.has(pid) ? next.delete(pid) : next.add(pid);
    return next;
  });

  const handleUpdateCp = async (propertyId, updates) => {
    await apiClient.put(`/clients/${id}/properties/${propertyId}`, updates);
    load();
  };

  const handleRemoveProp = async (propertyId) => {
    await apiClient.delete(`/clients/${id}/properties/${propertyId}`);
    load();
  };

  const toggleExpandPlan = async (planId) => {
    if (expandedPlanId === planId) { setExpandedPlanId(null); return; }
    setExpandedPlanId(planId);
    if (!planStops[planId]) {
      const r = await apiClient.get(`/plans/${planId}`);
      setPlanStops(prev => ({ ...prev, [planId]: r.data.data.stops?.slice().sort((a, b) => a.stop_order - b.stop_order) ?? [] }));
    }
  };

  const handleDeletePlan = async (planId) => {
    await apiClient.delete(`/plans/${planId}`);
    load();
  };

  const handleActivatePlan = async (planId) => {
    await apiClient.post(`/plans/${planId}/activate`);
    navigate('/today');
  };

  const handleCopyPlan = async () => {
    await apiClient.post(`/plans/${copyTarget.id}/copy`, { plan_date: copyDate, name: `${copyTarget.name} (copy)` });
    setCopyPlanOpen(false);
    load();
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!client) return null;

  const cps = client.clientProperties ?? [];
  const plans = client.plans ?? [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ mb: 2 }}>
        All Clients
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4">{client.first_name} {client.last_name}</Typography>
          <Typography color="text.secondary">
            {[client.email, client.phone].filter(Boolean).join(' · ')}
          </Typography>
          {client.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{client.notes}</Typography>}
        </Box>
        <Button
          variant="contained"
          startIcon={<MapIcon />}
          onClick={() => navigate(`/plan?client=${id}`)}
        >
          Plan a Day
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Properties (${cps.length})`} />
        <Tab label={`Plans (${plans.length})`} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" onClick={() => setAddPropOpen(true)}>Assign Property</Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Address</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Must Show</TableCell>
                  <TableCell>Anchor Time</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Visit History</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {cps.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center">No properties assigned yet.</TableCell></TableRow>
                )}
                {cps.map(cp => {
                  const propVisits = (client.visits ?? []).filter(v => v.property_id === cp.property_id)
                    .sort((a, b) => new Date(b.visited_at) - new Date(a.visited_at));
                  return (
                  <TableRow key={cp.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{cp.property?.address}</Typography>
                      <Typography variant="caption" color="text.secondary">{cp.property?.city}, {cp.property?.state}</Typography>
                    </TableCell>
                    <TableCell>{cp.property?.property_type}</TableCell>
                    <TableCell>${Number(cp.property?.price).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip label={cp.property?.status} size="small" color={statusColor(cp.property?.status)} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={cp.must_show ? 'Must show' : 'Optional'}>
                        <IconButton size="small" onClick={() => handleUpdateCp(cp.property_id, { must_show: !cp.must_show })}
                          sx={{ color: cp.must_show ? 'warning.main' : 'action.disabled' }}>
                          {cp.must_show ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="time"
                        size="small"
                        value={cp.anchor_time ?? ''}
                        onChange={e => handleUpdateCp(cp.property_id, { anchor_time: e.target.value || null })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        placeholder="default"
                        value={cp.duration_override ?? ''}
                        onChange={e => handleUpdateCp(cp.property_id, { duration_override: e.target.value ? Number(e.target.value) : null })}
                        InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      {propVisits.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Not yet visited</Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2">
                            {new Date(propVisits[0].visited_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                          {propVisits.length > 1 && (
                            <Chip label={`${propVisits.length} visits`} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleRemoveProp(cp.property_id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );})}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" onClick={() => navigate(`/plan?client=${id}`)}>New Plan</Button>
          </Box>

          {plans.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No plans yet. Click "Plan a Day" to create one.</Typography>
            </Paper>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {plans.map(plan => (
              <Paper key={plan.id} sx={{ overflow: 'hidden' }}>
                {/* Header row */}
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => toggleExpandPlan(plan.id)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">{plan.name}</Typography>
                      {expandedPlanId === plan.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {fmtDate(plan.plan_date)} · {plan.summary?.total_travel_minutes} min driving · {plan.summary?.total_showing_minutes} min showing
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip label={plan.status} size="small" color={{ draft: 'default', confirmed: 'primary', active: 'success', completed: 'secondary' }[plan.status]} />
                      <Chip label={plan.routing_source} size="small" variant="outlined" />
                      {plan.warnings?.length > 0 && <Chip label={`${plan.warnings.length} warning(s)`} size="small" color="warning" />}
                      {plan.dropped?.length > 0 && <Chip label={`${plan.dropped.length} dropped`} size="small" color="info" />}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {plan.status !== 'completed' && (
                      <Tooltip title={plan.status === 'active' ? 'Already active — go to Today' : 'Activate this plan'}>
                        <Button
                          size="small"
                          variant={plan.status === 'active' ? 'contained' : 'outlined'}
                          color="success"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => plan.status === 'active' ? navigate('/today') : handleActivatePlan(plan.id)}
                          sx={{ mr: 0.5 }}
                        >
                          {plan.status === 'active' ? 'Active' : 'Activate'}
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="View full plan">
                      <IconButton onClick={() => setViewPlanId(plan.id)}><OpenInFullIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Copy to new date">
                      <IconButton onClick={() => { setCopyTarget(plan); setCopyDate(''); setCopyPlanOpen(true); }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Select
                      size="small"
                      value={plan.status}
                      onChange={e => apiClient.put(`/plans/${plan.id}`, { status: e.target.value }).then(load)}
                      sx={{ minWidth: 110 }}
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="confirmed">Confirmed</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                    <IconButton color="error" onClick={() => handleDeletePlan(plan.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Expanded stop list */}
                {expandedPlanId === plan.id && (
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2, pb: 2 }}>
                    {!planStops[plan.id] ? (
                      <CircularProgress size={20} sx={{ mt: 1 }} />
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        {planStops[plan.id].map((stop, i) => (
                          <Box key={stop.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: stop.must_show ? '#ed6c02' : '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 11, flexShrink: 0 }}>{i + 1}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap fontWeight={500}>{stop.property?.address}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmt(stop.arrival_time)} → {fmt(stop.departure_time)} · {stop.duration_minutes} min
                              </Typography>
                            </Box>
                            {stop.must_show && <Chip label="must" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />}
                            {stop.anchored && <Chip label="anchored" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />}
                          </Box>
                        ))}
                        {planStops[plan.id].length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No stops recorded.</Typography>}
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Assign property dialog */}
      <Dialog open={addPropOpen} onClose={() => { setAddPropOpen(false); setSelectedPropIds(new Set()); setPropFilter(''); setPropTypeFilter(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Properties</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, pb: 1, display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search address…"
              value={propFilter}
              onChange={e => setPropFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <Select
              size="small"
              value={propTypeFilter}
              onChange={e => setPropTypeFilter(e.target.value)}
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All types</MenuItem>
              {[...new Set(unassignedProperties.map(p => p.property_type).filter(Boolean))].map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </Box>
          {selectedPropIds.size > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Chip label={`${selectedPropIds.size} selected`} size="small" color="primary" onDelete={() => setSelectedPropIds(new Set())} />
            </Box>
          )}
          <Divider />
          <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {unassignedProperties
              .filter(p => {
                const q = propFilter.toLowerCase();
                const matchesText = !q || p.address.toLowerCase().includes(q) || p.city.toLowerCase().includes(q);
                const matchesType = !propTypeFilter || p.property_type === propTypeFilter;
                return matchesText && matchesType;
              })
              .map(p => (
                <ListItem
                  key={p.id}
                  button
                  onClick={() => togglePropSelect(p.id)}
                  selected={selectedPropIds.has(p.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox checked={selectedPropIds.has(p.id)} size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${p.address}, ${p.city}`}
                    secondary={`${p.property_type} · ${p.bedrooms ?? '?'}bd · $${Number(p.price).toLocaleString()}`}
                  />
                  <Chip label={p.status} size="small" color={statusColor(p.status)} sx={{ ml: 1 }} />
                </ListItem>
              ))}
            {unassignedProperties.length === 0 && (
              <ListItem><ListItemText primary="All properties already assigned." /></ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddPropOpen(false); setSelectedPropIds(new Set()); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!selectedPropIds.size}>
            Assign {selectedPropIds.size > 0 ? `(${selectedPropIds.size})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy plan dialog */}
      <Dialog open={copyPlanOpen} onClose={() => setCopyPlanOpen(false)}>
        <DialogTitle>Copy Plan to New Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Copying: <strong>{copyTarget?.name}</strong></Typography>
          <TextField
            label="New date"
            type="date"
            value={copyDate}
            onChange={e => setCopyDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyPlanOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCopyPlan} disabled={!copyDate}>Copy</Button>
        </DialogActions>
      </Dialog>

      {/* Full plan view dialog */}
      <PlanView planId={viewPlanId} open={!!viewPlanId} onClose={() => setViewPlanId(null)} />
    </Box>
  );
};

export default ClientDetail;
